import React, { useState, useEffect } from 'react'
import { Box, Tab, Tabs, CircularProgress, Badge } from "@mui/material";
import { useIntl, FormattedMessage } from "react-intl";
import { useQuery } from "@tanstack/react-query";
import { styled } from "@mui/material/styles";
import useFeatureFlags, { isFeatureEnabled } from '../../../hooks/useFeatureFlags';
import { fetchModules } from "../../../fetchers/modules";
import { fetchMachineData } from "../../../fetchers/machine";
import { useAuth } from "../../../auth/AuthContext";
import { hasAnyRole } from "../../../auth/authUi";
import useModules, { isModuleEnabled } from "../../../hooks/useModules";
import { CMMS_MODULE } from "../../cmms/cmmsFeature";
import { CMMS_FIELD_ROLES } from "../../cmms/cmmsRoles";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import MachineWorkOrdersTabLabel from "./MachineWorkOrdersTabLabel";
import messages from "./messages";

const StyledTabs = styled((props) => (


    <Tabs
        {...props}
        TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
    />
))({
    '& .MuiTabs-indicator': {
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'transparent',
    },
    '& .MuiTabs-indicatorSpan': {
        width: '100%',
        backgroundColor: '#fff',
    },
});

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(

    ({ theme }) => ({

        textTransform: 'none',
        fontWeight: theme.typography.fontWeightRegular,
        fontSize: theme.typography.pxToRem(20),
        marginRight: theme.spacing(0),
        minWidth: "150px",
        //backgroundColor: '#799c4a',
        color: 'rgba(255, 255, 255, 0.7)',
        '&.Mui-selected': {
            color: '#fff',
            backgroundColor: "#434957 !important",
        },
        '&.Mui-focusVisible': {
            backgroundColor: 'rgba(100, 95, 228, 0.32)',
        },
        '&.MuiTab-root': {
            backgroundColor: '#43495799',
            border: "1px solid #0c101b",
        },
    }),
);

/*
 * The CMMS maintenance tab.
 *
 * Appended LAST, and that is not cosmetic. Every other optional tab here (SensorData,
 * Activities) is rendered `disabled` rather than omitted, which is why the `selectedTab ===
 * 0..5` blocks in MachineDetails have been able to hard-code their indexes. CMMS cannot follow
 * that convention — a greyed-out "Work orders" tab would advertise an unreleased module to
 * every OEE customer — so it is conditionally rendered instead, and a conditional tab anywhere
 * but last would shift every index below it the moment the flag is off.
 *
 * The index is exported so MachineDetails mounts the body and clamps the persisted selection
 * against the same number, and the availability hook so both agree on whether it exists at all.
 */
export const CMMS_WORK_ORDERS_TAB_INDEX = 7;

export function useIsCmmsWorkOrderTabAvailable() {
  const modulesQuery = useModules();
  const { roleIds } = useAuth();

  return {
    available: isModuleEnabled(modulesQuery, CMMS_MODULE) && hasAnyRole(roleIds, CMMS_FIELD_ROLES),
    // The clamp must not fire while the modules are still in flight, or a reload with the tab
    // selected bounces the user to another tab and back.
    isResolved: !modulesQuery.isLoading,
  };
}

export default function MachineTabs(props) {
    const { machineId, activitiesCount, selectedTab, setSelectedTab, alarmsCount } = props;
    const featureQuery = useFeatureFlags();
    const { formatMessage } = useIntl();
    const { available: cmmsTabAvailable } = useIsCmmsWorkOrderTabAvailable();
    const [companyKey] = useLocalStorage("companyKey", 1);

    const machineQuery = useQuery(
        ["machine", machineId],
        () => fetchMachineData(machineId),
        {
          keepPreviousData: true
        });

    const modulesQuery = useQuery(
        ["module"],
        () => fetchModules(),
        {
          staleTime: 120000,
          refetchInterval: 120000,
          keepPreviousData: true
        }
    );

    const activitiesEnabled = modulesQuery.data?.enabledModules.find(module => module.id === "activities") !== undefined;

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    if (featureQuery.isLoading || modulesQuery.isLoading) {
        return <div style={{display: 'flex',  justifyContent:'center', alignItems:'center', height: '100vh'}}><CircularProgress color="secondary" /></div>;
    }

    var showOnlyActivities = false;
    if (machineQuery.data?.machineType?.showInActivitySpecialList === true && selectedTab !== 2) {
        showOnlyActivities = true;
        handleTabChange(null, 2);
    }
    return (
      <Box sx={{ width: '100%' }} >
        <Box>
          <StyledTabs value={selectedTab} onChange={handleTabChange} aria-label="styled tabs example">
            { showOnlyActivities ? <StyledTab label={formatMessage(messages.uncodedStopsAndOpenActivities)} disabled /> : <StyledTab label={formatMessage(messages.uncodedStopsAndOpenActivities)} /> }
            { showOnlyActivities ? <StyledTab label={formatMessage(messages.tabMachineStops)} disabled /> : <StyledTab label={formatMessage(messages.tabMachineStops)} /> }
            { showOnlyActivities ? <StyledTab label={formatMessage(messages.tabProductionOrders)} disabled /> : isFeatureEnabled(featureQuery, "UseProductionOrderTableAsPOMaster") ? <StyledTab label={formatMessage(messages.tabProductionOrders)} /> : <StyledTab label={formatMessage(messages.tabProductionOrders)} disabled /> }
            { activitiesEnabled ? <StyledTab label={<Badge badgeContent={activitiesCount} color="warning" ><FormattedMessage {...messages.tabActivities} /></Badge>} /> : <StyledTab label={formatMessage(messages.tabActivities)} disabled />  }
            { showOnlyActivities ? <StyledTab label={formatMessage(messages.tabTakt)} disabled /> : <StyledTab label={formatMessage(messages.tabTakt)} />}
            { showOnlyActivities ? <StyledTab label={formatMessage(messages.tabSensorData)} disabled /> : isFeatureEnabled(featureQuery, "SensorData") ? <StyledTab label={formatMessage(messages.tabSensorData)} /> : <StyledTab label={formatMessage(messages.tabSensorData)} disabled />  }
            { showOnlyActivities ? <StyledTab label={formatMessage(messages.tabAlarms)} disabled /> : isFeatureEnabled(featureQuery, "SensorData") ? <StyledTab label={<Badge badgeContent={alarmsCount} color="warning" ><FormattedMessage {...messages.tabAlarms} /></Badge>} /> : <StyledTab label={formatMessage(messages.tabAlarms)} disabled />  }
            {/* The label owns its own reads (open count + tool service), so this file does not
                grow three hooks for one tab. See MachineWorkOrdersTabLabel. */}
            { cmmsTabAvailable && <StyledTab label={<MachineWorkOrdersTabLabel machineId={machineId} companyKey={companyKey} />} /> }
          </StyledTabs>
        </Box>
      </Box>
    );
}